// LEGACY LEFTOVER — the legacy Billing page itself is gone; this module survives only
// because BillingV2 (and Patient/Documents) import its shared billing vocabulary. It
// still exports page-era helpers that nothing uses. Slated to move under
// pages/BillingV2 (or src/model) and shed the dead exports — see
// docs/BILLING_LEGACY_CLEANUP.md.
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import type { Encounter } from '@medplum/fhirtypes'

dayjs.extend(utc)
dayjs.extend(timezone)

export const BILLING_DISPLAY_TIMEZONE = 'America/New_York'

export const BILLING_STATUS_TAG_SYSTEM =
  'https://behuman.health/fhir/CodeSystem/encounter-billing-status'
export const BILLING_ENTITY_TAG_SYSTEM =
  'https://behuman.health/fhir/CodeSystem/encounter-billing-entity'
export const BILLING_STATUS_UPDATED_AT_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/encounter-billing-status-updated-at'

// Mirror of the middleware encounter Claim Status value set. Source of truth:
// packages/middleware/src/care-coordination/dto/billing-status.constants.ts
// (frontends do not import middleware — keep this in sync with that file). The
// legacy codes `pending`/`billed` are retained and relabeled; the other five are new.
// `failed` is system-set only by the claim-submission pipeline (never by hand) — it does
// not appear in the manual status control's options (see BillingV2/utils.ts
// manualStatusOptions), only in the read-only pill/filter mappings below.
export const BILLING_STATUS_VALUES = [
  'pending',
  'ready-to-bill',
  'billed',
  'failed',
  'paid',
  'denied',
  'voided',
] as const

export type BillingStatusValue = (typeof BILLING_STATUS_VALUES)[number]

export const BILLING_STATUS_LABELS: Record<BillingStatusValue, string> = {
  pending: 'Pending Coding',
  'ready-to-bill': 'Ready to Bill',
  billed: 'Submitted',
  failed: 'Failed',
  paid: 'Paid',
  denied: 'Denied',
  voided: 'Voided',
}

export type BillingStatusStyle = { backgroundColor: string; borderColor: string; color: string }

export const BILLING_STATUS_STYLES: Record<BillingStatusValue, BillingStatusStyle> = {
  pending: { backgroundColor: '#fff4e8', borderColor: '#f6d8b8', color: '#d97706' },
  'ready-to-bill': { backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb' },
  billed: { backgroundColor: '#eef2ff', borderColor: '#c7d2fe', color: '#4f46e5' },
  // Same red family as `denied` but a darker, more urgent tone — distinguishes a system
  // submission failure from a payer's denial at a glance.
  failed: { backgroundColor: '#fef2f2', borderColor: '#fca5a5', color: '#b91c1c' },
  paid: { backgroundColor: '#e8fff1', borderColor: '#c9f2d8', color: '#31a24c' },
  denied: { backgroundColor: '#fff1f2', borderColor: '#fecdd3', color: '#e11d48' },
  voided: { backgroundColor: '#f1f5f9', borderColor: '#e2e8f0', color: '#64748b' },
}

export function isBillingStatusValue(code: string | null | undefined): code is BillingStatusValue {
  return Boolean(code) && (BILLING_STATUS_VALUES as readonly string[]).includes(code as string)
}

// Per-encounter billing summary backing the Billing Codes column. Mirrors the
// middleware `billingSummarySchema` output. Source of truth:
// packages/middleware/src/billing/handlers/billing-summaries.contract.ts
// (frontends do not import middleware — keep this in sync with that file).
export type BillingCodesSummary = {
  encounterId: string
  firstCode: string | null
  firstModifier: string | null
  serviceLineCount: number
  diagnosisCount: number
}

// Mirror of the middleware `billingDetailSchema` output backing the Billing
// Details drawer. Source of truth:
// packages/middleware/src/billing/handlers/billing-detail.contract.ts
// (frontends do not import middleware — keep this in sync with that file).
export type BillingDiagnosisDetail = {
  code: string | null
  display: string | null
  system: string | null
}

export type BillingServiceLineDetail = {
  code: string | null
  display: string | null
  system: string | null
  modifiers: string[]
  price: number | null
  quantity: number | null
  diagnoses: BillingDiagnosisDetail[]
}

export type BillingAuthoring = {
  createdOn: string | null
  createdBy: string | null
  modifiedOn: string | null
  modifiedBy: string | null
}

export type BillingDetail = {
  encounterId: string
  status: string | null
  // The claim's submitted date (preserved billing-status date), present only while
  // the status is Submitted. Null otherwise.
  submittedOn: string | null
  serviceLines: BillingServiceLineDetail[]
  encounterDiagnoses: BillingDiagnosisDetail[]
  authoring: BillingAuthoring | null
}

// A code with its optional modifier, e.g. "99204–25" (en-dash). Falls back to an
// em-dash when the code is absent. Shared by the Billing Codes column summary and
// the drawer's service lines so both read identically.
export function formatCodeWithModifier(
  code: string | null | undefined,
  modifier: string | null | undefined,
): string {
  const displayCode = code ?? '—'
  return modifier ? `${displayCode}–${modifier}` : displayCode
}

export function formatServiceLineCode(line: BillingServiceLineDetail): string {
  return formatCodeWithModifier(line.code, line.modifiers[0] ?? null)
}

// Derive the compact Billing Codes column summary from a full billing detail, so
// the dashboard row can update in place after a save without refetching summaries.
// `diagnosisCount` matches the batched summary endpoint's count: unique diagnoses
// across all service lines (deduped by system|code|display).
export function summaryFromDetail(detail: BillingDetail): BillingCodesSummary {
  const firstLine = detail.serviceLines[0]
  const uniqueDiagnoses = new Set(
    detail.serviceLines.flatMap((line) =>
      line.diagnoses.map((dx) => `${dx.system ?? ''}|${dx.code ?? ''}|${dx.display ?? ''}`),
    ),
  )

  return {
    encounterId: detail.encounterId,
    firstCode: firstLine?.code ?? null,
    firstModifier: firstLine?.modifiers[0] ?? null,
    serviceLineCount: detail.serviceLines.length,
    diagnosisCount: uniqueDiagnoses.size,
  }
}

// A diagnosis for display in the drawer: "E11.9 · Type 2 diabetes", or whichever
// of code/display is present.
export function formatBillingDiagnosis(diagnosis: BillingDiagnosisDetail): string {
  return [diagnosis.code, diagnosis.display].filter(Boolean).join(' · ') || '—'
}

export const BILLING_ADD_CODES_LABEL = '+ Add codes'

// Compact one-line summary for the Billing Codes column:
//   no codes         → "+ Add codes"
//   one service line → "99204–25 · 3 diagnoses" (modifier omitted when absent)
//   multiple lines   → "99204–25 + 2 more"
export function formatBillingCodesSummary(summary: BillingCodesSummary | undefined): string {
  if (!summary || summary.serviceLineCount === 0) {
    return BILLING_ADD_CODES_LABEL
  }

  const codeWithModifier = formatCodeWithModifier(summary.firstCode, summary.firstModifier)

  if (summary.serviceLineCount === 1) {
    const count = summary.diagnosisCount
    return `${codeWithModifier} · ${count} ${count === 1 ? 'diagnosis' : 'diagnoses'}`
  }

  return `${codeWithModifier} + ${summary.serviceLineCount - 1} more`
}

export type BillingEntityValue = 'FL' | 'NJ' | 'NY'

export const BILLING_ENTITY_OPTIONS: ReadonlyArray<{ value: BillingEntityValue; label: string }> = [
  { value: 'FL', label: 'Florida, beHuman Medical, PA' },
  { value: 'NJ', label: 'New Jersey beHuman Medical, NJ' },
  { value: 'NY', label: 'New York beHuman Medical Group, NY' },
]

export function getEncounterBillingEntity(encounter: Encounter): BillingEntityValue | null {
  const tag = encounter.meta?.tag?.find((t) => t.system === BILLING_ENTITY_TAG_SYSTEM)
  const code = tag?.code
  if (code === 'FL' || code === 'NJ' || code === 'NY') return code
  return null
}

export function formatDateForMiddleware(dateTime?: string): string {
  if (!dateTime) {
    return 'N/A'
  }

  const date = dayjs(dateTime).tz(BILLING_DISPLAY_TIMEZONE)
  if (!date.isValid()) {
    return 'N/A'
  }

  return date.format('MMM D, YYYY')
}

// Return a copy of the encounter with its Claim Status tag set to `status`,
// preserving every other tag. Used to reflect a drawer status change in the
// dashboard row's local state without refetching. Mirrors the middleware write
// (same tag system + label from `BILLING_STATUS_LABELS`).
export function withEncounterBillingStatus(
  encounter: Encounter,
  status: BillingStatusValue,
): Encounter {
  const tag = [
    ...(encounter.meta?.tag ?? []).filter((t) => t.system !== BILLING_STATUS_TAG_SYSTEM),
    { system: BILLING_STATUS_TAG_SYSTEM, code: status, display: BILLING_STATUS_LABELS[status] },
  ]
  return { ...encounter, meta: { ...encounter.meta, tag } }
}

export function getEncounterBillingUpdatedAt(encounter: Encounter): string | null {
  const billingExtension = encounter.extension?.find(
    (extension) => extension.url === BILLING_STATUS_UPDATED_AT_EXTENSION_URL,
  )

  return billingExtension?.valueDateTime ?? null
}

export type AppointmentStatusValue =
  | 'proposed'
  | 'pending'
  | 'booked'
  | 'arrived'
  | 'fulfilled'
  | 'cancelled'
  | 'noshow'
  | 'entered-in-error'
  | 'checked-in'
  | 'waitlist'

export type ProgressNoteStatusValue = 'submitted' | 'not-submitted'

export type AppointmentStatus = 'confirmed' | 'pending' | 'completed' | 'no-show'

export const APPOINTMENT_STATUS_STYLES: Record<
  AppointmentStatus,
  { backgroundColor: string; borderColor: string; color: string }
> = {
  confirmed: { backgroundColor: '#e8fff1', borderColor: '#c9f2d8', color: '#31a24c' },
  pending: { backgroundColor: '#fefce8', borderColor: '#fde68a', color: '#b45309' },
  completed: { backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb' },
  'no-show': { backgroundColor: '#fff1f2', borderColor: '#fecdd3', color: '#e11d48' },
}

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  confirmed: 'Confirmed',
  pending: 'Pending',
  completed: 'Completed',
  'no-show': 'No Show',
}

const FHIR_STATUS_MAP: Record<string, AppointmentStatus> = {
  booked: 'confirmed',
  pending: 'pending',
  fulfilled: 'completed',
  noshow: 'no-show',
}

export function mapAppointmentStatus(fhirStatus?: string): AppointmentStatus | null {
  return fhirStatus ? (FHIR_STATUS_MAP[fhirStatus] ?? null) : null
}
